import { getSnapshot as isReachable } from "./reachability.ts";

/**
 * Offline stance every data-bearing route must declare via `staticData.offline`:
 * - "ready"        — the screen works offline (prepared dataset / offline-tolerant).
 * - "preparable"   — blocked offline today, but a prepared dataset exists or is
 *                    planned; the unavailable panel points at the Offline Center.
 * - "online-only"  — blocked offline by design (will never be preparable); the
 *                    panel says the feature requires a connection.
 *
 * An absent declaration means online-only (default-closed). See
 * docs/KeyDecisions/2026-07-06-offline-feature-gating-convention.md.
 */
export type OfflineRouteStance = "ready" | "preparable" | "online-only";

declare module "@tanstack/react-router" {
	interface StaticDataRouteOption {
		offline?: OfflineRouteStance;
	}
}

export class OfflineUnavailableError extends Error {
	name = "OfflineUnavailableError";
	constructor(
		public readonly stance: Exclude<
			OfflineRouteStance,
			"ready"
		> = "online-only",
	) {
		super("Route is not available offline");
	}
}

/**
 * Route-level screen guard for non-offline-ready routes. Throw from
 * `beforeLoad` so neither the loader nor the component's suspense queries ever
 * run while unreachable — that is what prevents the never-settling
 * `ensureQueryData` hang (networkMode "offlineFirst" pauses instead of
 * erroring). The error carries the route's declared stance so the
 * unavailable panel can render the right copy without a second lookup.
 */
export function requireReachableRoute(
	stance: Exclude<OfflineRouteStance, "ready"> = "online-only",
): void {
	if (!isReachable()) throw new OfflineUnavailableError(stance);
}
